import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { ProtectedApp } from './AppShell';
import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  HttpLink,
  InMemoryCache,
  Reference,
} from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import env from './environment';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';
import 'react-toastify/dist/ReactToastify.css';
import { ErrorLink } from '@apollo/client/link/error';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import {
  createFragmentRegistry,
  defaultDataIdFromObject,
} from '@apollo/client/cache';
import './styles.css';
import { tap } from 'rxjs';
import { AuthenticatedProvider } from './providers/authenticated';
import { notify } from './utility/notify';

const SENSITIVE_KEY = /verification|plaintext|password|token|secret|nonce/i;

const redactSensitive = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(redactSensitive);

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        SENSITIVE_KEY.test(key) ? '[Filtered]' : redactSensitive(val),
      ])
    );
  }

  return value;
};

// Query strings carry single-use tokens (verify-email, login magic links),
// so keep them out of every tracking payload.
const stripQuery = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);

    parsed.search = '';

    return parsed.toString();
  } catch {
    return url.split('?')[0] ?? url;
  }
};

// Navigation/fetch breadcrumbs record the URLs they touch — drop the query.
const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  if (!breadcrumb.data) return breadcrumb;

  const data: Record<string, unknown> = { ...breadcrumb.data };

  for (const key of ['url', 'to', 'from']) {
    const value = data[key];
    if (typeof value === 'string') data[key] = stripQuery(value);
  }

  return {
    ...breadcrumb,
    data: redactSensitive(data) as typeof breadcrumb.data,
  };
};

// A request body may arrive as a JSON string (the GraphQL POST body) rather
// than a parsed object — redact sensitive variables in either shape.
const redactMaybeJson = (value: unknown): unknown => {
  if (typeof value === 'string') {
    try {
      return JSON.stringify(redactSensitive(JSON.parse(value)));
    } catch {
      return value;
    }
  }

  return redactSensitive(value);
};

// The event URL and the GraphQL client integration's request variables both
// carry tokens (confirmEmail's `verification`, login's `token`).
const scrubSentryEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
  if (event.request?.url) event.request.url = stripQuery(event.request.url);

  if (event.request) delete event.request.query_string;

  if (event.request?.data) {
    event.request.data = redactMaybeJson(
      event.request.data
    ) as typeof event.request.data;
  }

  if (event.contexts) {
    event.contexts = redactSensitive(event.contexts) as typeof event.contexts;
  }

  return event;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

declare module '@apollo/client' {
  interface DefaultContext {
    isBatched?: boolean;
    accessToken?: string | undefined;
  }
}

const httpLink = (uri: string) =>
  ApolloLink.split(
    (op) => op.getContext().isBatched ?? false,
    new BatchHttpLink({
      uri,
      batchInterval: 20,
      credentials: 'include',
    }),
    new HttpLink({
      uri,
      credentials: 'include',
    })
  );

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const err of error.errors) {
      switch (err.extensions?.code) {
        case 'FORBIDDEN': {
          localStorage.clear();

          sessionStorage.clear();

          window.location.pathname = '/login';

          break;
        }
        case 'INTERNAL_SERVER_ERROR': {
          notify.error(error.message);

          break;
        }
      }
    }
  } else if (CombinedProtocolErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) =>
      console.log(
        `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(
          extensions
        )}`
      )
    );
  }
});

const authLink = new ApolloLink((operation, forward) => {
  const _accessToken = client.defaultContext.accessToken;
  const _refreshToken = localStorage.getItem('x-sc-r');

  operation.setContext({
    headers: {
      authorization: _accessToken ? `Bearer ${_accessToken}` : undefined,
      ...(_refreshToken ? { 'sc-r': _refreshToken } : {}),
    },
  });

  return forward(operation).pipe(
    tap(() => {
      const context = operation.getContext();

      if (context.response) {
        // eslint-disable-next-line
        const accessToken = context.response.headers.get('x-sc-a');

        // eslint-disable-next-line
        const refreshToken = context.response.headers.get('x-sc-r');

        if (typeof accessToken === 'string') {
          localStorage.setItem('sc-logged', String(new Date().getTime()));

          client.defaultContext.accessToken = accessToken;
        }

        if (typeof refreshToken === 'string') {
          localStorage.setItem('x-sc-r', refreshToken);
        }
      }
    })
  );
});

export const client = new ApolloClient({
  cache: new InMemoryCache({
    fragments: createFragmentRegistry(),
    typePolicies: {
      Query: {
        fields: {
          indexIntegrationDocuments: {
            keyArgs: ['input', ['type', 'query', 'pathSegments']],
            merge(
              existing: { items: readonly Reference[] } | undefined,
              incoming: { items: readonly Reference[] },
              { args, readField }
            ) {
              const input = args?.input as
                | { nextPageToken?: string | null }
                | undefined;

              if (!input?.nextPageToken) return incoming;

              const items = [...(existing?.items ?? [])];
              const seen = new Set(
                items.map((item) => readField<string>('id', item))
              );

              for (const item of incoming.items) {
                if (!seen.has(readField<string>('id', item))) items.push(item);
              }

              return { ...incoming, items };
            },
          },
        },
      },
    },
    dataIdFromObject(responseObject) {
      if (typeof responseObject.uuid === 'string')
        return `${responseObject.__typename}:${String(responseObject.uuid)}`;

      return defaultDataIdFromObject(responseObject);
    },
  }),
  defaultContext: {},
  link: ApolloLink.from([errorLink, authLink, httpLink(env.apiUrl)]),
});

Sentry.init({
  dsn: 'https://eec5c52dca6b0d82ff1ba2adfb679e88@o4510359150002176.ingest.de.sentry.io/4510359157997648',
  environment: env.sentry.environment,
  release: import.meta.env.PUBLIC_SENTRY_RELEASE
    ? `frontend@${import.meta.env.PUBLIC_SENTRY_RELEASE}`
    : 'frontend@dev',
  integrations: [
    Sentry.graphqlClientIntegration({
      endpoints: [env.apiUrl],
    }),
  ],
  beforeSend: scrubSentryEvent,
  beforeBreadcrumb: scrubBreadcrumb,
});

Sentry.setTag('app', 'frontend');

root.render(
  <StrictMode>
    <ToastContainer />
    <ApolloProvider client={client}>
      <AuthenticatedProvider>
        <ProtectedApp />
      </AuthenticatedProvider>
    </ApolloProvider>
  </StrictMode>
);
